import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { UserDocument } from "../../models/UserDocument";
import { Modes } from "./types";
import { UserDocumentDialog } from "./UserDocumentDialog";
import { AddItemCard } from "./AddItemCard";
import { DocumentItemWrapper } from "./DocumentItemWrapper";
import { documentsAPI } from "apis";
import { Loading } from "views/shared/Loading";
import { download } from "utils/download";
import urlSlug from "url-slug";
import { useFileCabinetContext } from "FileCabinetContextWrapper";
import { useAuthContext } from "AuthContextWrapper";

export function FileCabinetView(): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<UserDocument | null>(null);
  const [selectedDocumentSrc, setSelectedDocumentSrc] = useState<string | undefined>();
  const [dialogMode, setDialogMode] = useState<Modes | undefined>();
  const { refreshDocs, loadingDocs, docs, setLoadingDocs } = useFileCabinetContext();
  const { activeEstate, user } = useAuthContext()
  const getDefaultActiveDoc = (): UserDocument => {
    const initialDoc = new UserDocument();
    initialDoc.documentTypeId = 1; // includeOnlyDocumentTypeIds && includeOnlyDocumentTypeIds.length > 0 ? includeOnlyDocumentTypeIds[0] : 1
    return initialDoc;
  };

  useEffect(() => {
    refreshDocs().then(() => setLoadingDocs(false));
  }, []);

  return (
    <>
      {selectedDocument && (
        <UserDocumentDialog
          open={isOpen}
          mode={dialogMode}
          onUpdateDoc={(shouldClose = true) => {
            refreshDocs();
            if (shouldClose) {
              setSelectedDocumentSrc(undefined);
              setSelectedDocument(null);
              setDialogMode(undefined);
              setIsOpen(false);
            }
          }}
          onComplete={(shouldClose = true) => {
            refreshDocs();
            if (shouldClose) {
              setSelectedDocumentSrc(undefined);
              setSelectedDocument(null);
              setDialogMode(undefined);
              setIsOpen(false);
            }
          }}
          onCancel={() => {
            setIsOpen(false);
            setSelectedDocumentSrc(undefined);
            setSelectedDocument(null);
            setDialogMode(undefined);
          }}
          document={selectedDocument}
          documentSrc={selectedDocumentSrc || ""}
          documentId={selectedDocument?.id}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          paddingTop: "0",
        }}
      >
        <AddItemCard
          title="Upload a document"
          onClick={() => {
            setSelectedDocument(getDefaultActiveDoc());
            setSelectedDocumentSrc(undefined);
            setIsOpen(true);
          }}
        />
        {loadingDocs ? (
          <div
            style={{ marginTop: "75px", marginLeft: "50px" }}
            color="primary"
          >
            <Loading />
            <Typography variant="body1">Loading documents...</Typography>
          </div>
        ) : (
          docs?.map((doc: UserDocument) => {
            return (
              <Card
                sx={{
                  my: 2,
                  mr: 1,
                  width: {
                    sm: 197,
                    xs: "100%",
                  },
                  transition: "box-shadow 0.1s ease-in-out",
                  ":hover": {
                    boxShadow: 10, // theme.shadows[20]
                  },
                }}
                key={`doc_${doc.id}_${doc.version?.id}`}
              >
                <CardContent
                  sx={{
                    p: 0,
                    "&:last-child": {
                      pb: 0,
                    },
                  }}
                >
                  <DocumentItemWrapper
                    document={doc}
                    onClick={(docSrc: string | undefined) => {
                      setSelectedDocument(doc);
                      setSelectedDocumentSrc(docSrc);
                      setIsOpen(true);
                    }}
                    onDelete={(docSrc: string | undefined) => {
                      setDialogMode(Modes.delete);
                      setSelectedDocument(doc);
                      setSelectedDocumentSrc(docSrc);
                      setIsOpen(true);
                    }}
                    onDownload={async () => {
                      if (!doc?.id) {
                        console.warn("not sure which file this is");
                        return;
                      }

                      if (!activeEstate?.id || !user?.id) {
                        return
                      }
                      
                      const url = await documentsAPI.fetchUrl(activeEstate.id, user.id, doc.id);
                      if (!url) {
                        console.warn(
                          "unable to get download url for file " + doc.id
                        );
                        return;
                      }

                      let filename = "atticus-file." + (doc.version?.ext || "unknown-file-type");
                      if (doc.version?.title) {
                        filename = urlSlug(doc.version?.title) + "." + (doc.version?.ext || "unknown-file-type");
                      }

                      download(url, filename); // I'm pretty sure filename here will be ignored in lieu of the Content-Disposition response header from the backend
                    }}
                  />
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>
    </>
  );
}
